import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/api-client';
import type { ApiClientError, ApiSuccess } from '@/lib/api-client';
import {
  approveMaster,
  activateMaster,
  createMaster,
  listMaster,
  proposeMasterChange,
  purgeMaster,
  rejectMaster,
  retireMaster,
  updateMasterDirect,
  type MasterListParams,
  type MasterListResult,
  type ProposeChangeResult,
} from '@/lib/api-masters';
import type {
  IncomingItem,
  IncomingItemCreateInput,
  IncomingItemRouteUpdateInput,
  IncomingItemUpdateInput,
} from './types';

const KEY = 'incoming-items' as const;

export const useIncomingItemsList = (params: MasterListParams = {}) => {
  return useQuery<MasterListResult<IncomingItem>, ApiClientError>({
    queryKey: [KEY, 'list', params],
    queryFn: () => listMaster<IncomingItem>('incoming-items', params),
    placeholderData: (prev) => prev,
  });
};

export const useCreateIncomingItem = () => {
  const qc = useQueryClient();
  return useMutation<IncomingItem, ApiClientError, IncomingItemCreateInput>({
    mutationFn: (body) =>
      createMaster<IncomingItem, IncomingItemCreateInput>('incoming-items', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const useApproveIncomingItem = () => {
  const qc = useQueryClient();
  return useMutation<IncomingItem, ApiClientError, { id: string; reason: string }>({
    mutationFn: ({ id, reason }) => approveMaster<IncomingItem>('incoming-items', id, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const useRejectIncomingItem = () => {
  const qc = useQueryClient();
  return useMutation<IncomingItem, ApiClientError, { id: string; reason: string }>({
    mutationFn: ({ id, reason }) => rejectMaster<IncomingItem>('incoming-items', id, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const useRetireIncomingItem = () => {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: (id) => retireMaster('incoming-items', id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const useActivateIncomingItem = () => {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: (id) => activateMaster('incoming-items', id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const usePurgeIncomingItem = () => {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: (id) => purgeMaster('incoming-items', id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

/**
 * Update only the `default_route_type` field on an existing incoming
 * item master row. Used by the master edit dialog's "standard sorting
 * route" Select.
 */
export const useUpdateIncomingItemRoute = () => {
  const qc = useQueryClient();
  return useMutation<
    IncomingItem,
    ApiClientError,
    { id: string; body: IncomingItemRouteUpdateInput }
  >({
    mutationFn: async ({ id, body }) => {
      const res = await apiClient.put<ApiSuccess<IncomingItem>>(
        `/masters/incoming-items/${id}/route`,
        body,
      );
      return res.data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

/**
 * Manager-only direct edit. `PUT /api/masters/incoming-items/:id`.
 */
export const useUpdateIncomingItemDirect = () => {
  const qc = useQueryClient();
  return useMutation<IncomingItem, ApiClientError, { id: string; body: IncomingItemUpdateInput }>({
    mutationFn: ({ id, body }) =>
      updateMasterDirect<IncomingItem, IncomingItemUpdateInput>('incoming-items', id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

/**
 * Supervisor+ change-proposal. Returns the created audit_approval row.
 */
export const useProposeIncomingItemChange = () => {
  const qc = useQueryClient();
  return useMutation<
    ProposeChangeResult,
    ApiClientError,
    { id: string; body: { proposed: IncomingItemUpdateInput; reason: string } }
  >({
    mutationFn: ({ id, body }) =>
      proposeMasterChange<IncomingItemUpdateInput>('incoming-items', id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};
